import Close20 from '@carbon/icons-react/es/close/20';
import { message } from 'antd';
import { keyBy } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectId, ProjectPhaseId } from 'api/models';
import { ProjectSource } from 'api/project';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { DateValue } from 'components/atoms/due-date-picker';
import { PageLoading } from 'components/atoms/loading';
import { TemplatesAPI } from 'data/templates/api';
import { TaskTemplateLight } from 'data/templates/tasks';
import { useInviteUserToProject } from 'hooks/use-invite-user';
import { useTranslate } from 'hooks/use-translation';
import {
  useAddTaskTemplateMutation,
  useGetProjectPhaseQuery,
  useUserCreatedTaskTemplatesQuery,
} from 'queries';
import { DrawerEmptyState } from './drawer-empty-state';
import { CheckBoxValue, DrawerFooter, DrawerPhase } from './drawer-footer';
import { DrawerFirstPhase } from './first-phase';
import { DrawerSecondPhase } from './second-phase';
import { PhaseNameWrapper } from './styles';

interface DrawerProps {
  visible: boolean;
  onClose: () => void;
  sourceTemplates?: ProjectSource[];
  projectId: number;
  phaseId: number;
  phaseName: string;
}

interface TaskTemplateWithDate {
  templateId: number | null;
  date?: DateValue;
}

export const TaskTemplateDrawer: FC<DrawerProps> = ({
  visible,
  onClose,
  sourceTemplates,
  projectId,
  phaseId,
  phaseName,
}) => {
  const translate = useTranslate();

  const { mutate, isLoading: isAddInProgress } = useAddTaskTemplateMutation(
    ProjectId(projectId),
  );

  const { data: projectPhase } = useGetProjectPhaseQuery(
    ProjectPhaseId(phaseId),
  );

  const { data: taskTemplates, isLoading: isGetTaskTemplatesInProgress } =
    useUserCreatedTaskTemplatesQuery();

  const handle = useInviteUserToProject(ProjectId(projectId), {
    confirmModalTextKeys: {
      content: 'invitation.via.mention.confirm.content',
      okText: 'invite_and_mention',
    },
  });

  const initialPhaseDates: (DateValue | undefined)[] = useMemo(
    () => [projectPhase?.startDate, projectPhase?.dueDate],
    [projectPhase?.dueDate, projectPhase?.startDate],
  );

  const [modifiedPhaseDates, setModifiedPhaseDates] =
    useState<(DateValue | undefined)[]>(initialPhaseDates);
  const [currentMax, setCurrentMax] = useState<TaskTemplateWithDate>({
    templateId: null,
    date: initialPhaseDates[1],
  });
  const [currentMin, setCurrentMin] = useState<TaskTemplateWithDate>({
    templateId: null,
    date: initialPhaseDates[0],
  });
  const [searchableTemplates, setSearchableTemplates] = useState(taskTemplates);
  const [checkBoxValues, setCheckBoxValues] = useState<CheckBoxValue[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    TaskTemplateLight[]
  >([]);
  const [drawerPhase, setDrawerPhase] = useState<DrawerPhase>(
    DrawerPhase.PhaseOne,
  );
  const [currentShowingTemplate, setCurrentShowingTemplate] =
    useState<TaskTemplateLight | null>(null);

  const isTaskTemplatesLoaded = (taskTemplates ?? []).length > 0;
  const noneSelected = !checkBoxValues.some((value) => value.checked);

  useEffect(() => {
    if (projectPhase) {
      setModifiedPhaseDates([projectPhase.startDate, projectPhase.dueDate]);
      setCurrentMax((prevMax) => {
        return { ...prevMax, date: projectPhase.dueDate };
      });
      setCurrentMin((prevMax) => {
        return { ...prevMax, date: projectPhase.startDate };
      });
    }
  }, [projectPhase]);

  useEffect(() => {
    if (taskTemplates) {
      setSearchableTemplates(taskTemplates);
      setCheckBoxValues(
        taskTemplates.map((template) => ({
          template,
          checked: false,
        })),
      );
    }
  }, [taskTemplates]);

  const onDatePickerChange = async (
    values: [DateValue, DateValue],
    taskTemplate: TaskTemplateLight,
  ) => {
    const template = { ...taskTemplate };

    if (template) {
      [template.startDate, template.endDate] = values;

      if (template.startDate != null && template.endDate == null) {
        const { dueDate } = await TemplatesAPI.getDueDate({
          templateId: template.templateId,
          startDate: template.startDate,
        });

        template.endDate = dueDate;
      }

      if (template.startDate) {
        const newStart = moment(template.startDate);

        if (newStart.isBefore(initialPhaseDates[0])) {
          if (
            template.templateId === currentMin.templateId ||
            newStart.isBefore(currentMin.date)
          ) {
            setCurrentMin({
              templateId: template.templateId,
              date: template.startDate,
            });
            setModifiedPhaseDates([template.startDate, modifiedPhaseDates[1]]);
          }
        } else if (template.templateId === currentMin.templateId) {
          setCurrentMin({ templateId: null, date: initialPhaseDates[0] });
          setModifiedPhaseDates([initialPhaseDates[0], modifiedPhaseDates[1]]);
        }
      }

      if (template.endDate) {
        const newEnd = moment(template.endDate);

        if (newEnd.isAfter(initialPhaseDates[1])) {
          if (
            template.templateId === currentMax.templateId ||
            newEnd.isAfter(currentMax.date)
          ) {
            setCurrentMax({
              templateId: template.templateId,
              date: template.endDate,
            });
            setModifiedPhaseDates([modifiedPhaseDates[0], template.endDate]);
          }
        } else if (template.templateId === currentMax?.templateId) {
          setCurrentMax({ templateId: null, date: initialPhaseDates[1] });
          setModifiedPhaseDates([modifiedPhaseDates[0], initialPhaseDates[1]]);
        }
      }

      if (template.startDate == null && template.endDate == null) {
        if (template.templateId === currentMax?.templateId) {
          setCurrentMax({ templateId: null, date: initialPhaseDates[1] });
          setModifiedPhaseDates([modifiedPhaseDates[0], initialPhaseDates[1]]);
        }

        if (template.templateId === currentMin?.templateId) {
          setCurrentMin({ templateId: null, date: initialPhaseDates[0] });
          setModifiedPhaseDates([initialPhaseDates[0], modifiedPhaseDates[1]]);
        }
      }

      setSelectedTemplates((prevState) => {
        const newState = prevState.map((item) => {
          const modifiedItem = { ...item };

          if (item.templateId === template.templateId) {
            modifiedItem.startDate = template.startDate;
            modifiedItem.endDate = template.endDate;
          }

          return modifiedItem;
        });

        return newState;
      });
    }
  };

  const onChangeCheckbox = (template: TaskTemplateLight) => {
    setCheckBoxValues((checkBoxVal) => {
      return checkBoxVal.map((t) => {
        if (t.template.templateId === template.templateId) {
          return {
            ...t,
            checked: !t.checked,
          };
        }

        return t;
      });
    });
  };

  const onDrawerClose = useCallback(() => {
    onClose();
    setSelectedTemplates([]);
    setDrawerPhase(DrawerPhase.PhaseOne);
    setCheckBoxValues(
      (taskTemplates ?? []).map((template) => ({
        template,
        checked: false,
      })),
    );
    setCurrentShowingTemplate(null);
  }, [onClose, taskTemplates]);

  const onClickNext = async () => {
    if (drawerPhase === DrawerPhase.PhaseOne) {
      setDrawerPhase(DrawerPhase.PhaseTwo);
      setSelectedTemplates(
        checkBoxValues.filter((t) => t.checked).map((t) => t.template),
      );
    } else {
      const templateData = selectedTemplates.map((template) => ({
        templateId: template.templateId,
        startDate: template.startDate,
        endDate: template.endDate,
        users: template.assignee,
        projectId,
        phaseId,
      }));
      const selectedTemplatesUsers = selectedTemplates.reduce((pre, curr) => {
        const users = keyBy(curr.assignee?.users || [], 'userId');

        return {
          ...pre,
          ...users,
        };
      }, {});

      await Object.keys(selectedTemplatesUsers).reduce(async (a, userId) => {
        await a;
        await handle([Number(userId)]);
      }, Promise.resolve());

      const singleTaskOnly = templateData.length === 1;
      const errorText = singleTaskOnly
        ? translate('projects.task_template.drawer.add_task.error')
        : translate('projects.task_template.drawer.add_tasks.error');
      const successText = singleTaskOnly
        ? translate('projects.task_template.drawer.add_task.success')
        : translate('projects.task_template.drawer.add_tasks.success');

      mutate(templateData, {
        onSuccess: () => {
          message.success(successText);
          onDrawerClose();
        },
        onError: () => {
          message.error(errorText);
        },
      });
    }
  };

  return (
    <CustomLayoutDrawer
      width={392}
      closeIcon={<Close20 />}
      visible={visible}
      onClose={onDrawerClose}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          {translate('projects.task_template.drawer.header.title')}
          <PhaseNameWrapper>{phaseName}</PhaseNameWrapper>
        </CustomLayoutDrawerContent.Title>
        <>
          {isTaskTemplatesLoaded ? (
            <>
              {drawerPhase === DrawerPhase.PhaseOne ? (
                <DrawerFirstPhase
                  sourceTemplates={sourceTemplates}
                  changeCurrentProjectTo={setCurrentShowingTemplate}
                  taskTemplates={taskTemplates}
                  currentShowingTemplate={currentShowingTemplate}
                  setSearchableTemplates={setSearchableTemplates}
                  searchableTemplates={searchableTemplates}
                  onChangeCheckbox={onChangeCheckbox}
                  checkBoxValues={checkBoxValues}
                  setCheckBoxValues={setCheckBoxValues}
                />
              ) : (
                <DrawerSecondPhase
                  selectedTemplates={selectedTemplates}
                  onDatePickerChange={onDatePickerChange}
                  initialPhaseDates={initialPhaseDates}
                />
              )}
            </>
          ) : isGetTaskTemplatesInProgress ? (
            <PageLoading />
          ) : (
            <DrawerEmptyState />
          )}
        </>
        <CustomLayoutDrawerContent.Footer>
          <DrawerFooter
            isTaskTemplatesLoaded={
              isTaskTemplatesLoaded || isGetTaskTemplatesInProgress
            }
            drawerPhase={drawerPhase}
            moveToPhase={setDrawerPhase}
            checkBoxValues={checkBoxValues}
            setCheckBoxValues={setCheckBoxValues}
            onClose={onClose}
            onClickNext={onClickNext}
            noneSelected={noneSelected}
            selectedTemplates={selectedTemplates}
            isAddInProgress={isAddInProgress}
            projectPhase={projectPhase}
            initialPhaseDates={initialPhaseDates}
            modifiedPhaseDates={modifiedPhaseDates}
          />
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
